import React, { useState, useMemo, useEffect } from 'react'
import { 
  useGetUsersQuery, 
  useGetUserStatsQuery, 
  useUpdateUserStatusMutation, 
  useDeleteUserMutation,
  useSearchUsersQuery,
  useGetVerificationUrlQuery,
  useLazyGetScopeReportLinkQuery,
  useLazyGetReshuffleReportLinkQuery,
  useLazyGetStartupReportLinkQuery,
  useLazyGetRiskIntelligenceReportLinkQuery,
  useLazyGetTrialResourcesLinkQuery,
  useGetPendingScopeReportEmailQuery,
  useGetPendingStartupReportEmailQuery,
  useGetPendingRiskIntelligenceReportEmailQuery,
  useGetPendingTrialResourcesEmailQuery,
  useCompleteScopeReportEmailVerificationMutation,
  useResetUserPasswordMutation,
  useGetUserAccountInfoQuery,
  useUpdateUserEnterprisePlanMutation,
  useGetUserSeatInfoQuery,
  useUpdateUserSeatsMutation,
  useCreateEnterpriseInvoiceMutation,
  useGetUserOrganizationsQuery,
  useToggleMotionodeSupportMutation,
  useToggleMotionodeSalesMutation,
  useToggleStripeTestingMutation,
  useSwitchAccountStatusMutation,
  useGenerateTemporaryAccessPasswordMutation
} from '../store/api/usersApi'
import StatsCard from '../components/StatsCard'
import SortableTableHeader from '../components/SortableTableHeader'
import { formatDateTimeEST, formatDateEST } from '../utils/dateUtils'

interface UserFilters {
  page: number;
  limit: number;
  search: string;
  status: 'all' | 'active' | 'inactive';
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  exclude_ghost?: number;
}

const Users: React.FC = () => {
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    limit: 20,
    search: '',
    status: 'all',
    sortBy: 'created_at',
    sortOrder: 'DESC',
    exclude_ghost: 1
  });

  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [verificationUserId, setVerificationUserId] = useState<number | null>(null);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<number | null>(null);
  const [resetPasswordMode, setResetPasswordMode] = useState<'random' | 'custom'>('random');
  const [customPassword, setCustomPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [enterpriseUserId, setEnterpriseUserId] = useState<number | null>(null);
  const [seatsValue, setSeatsValue] = useState<string>('');
  const [editSeatsUserId, setEditSeatsUserId] = useState<number | null>(null);
  const [showSalesWelcomeModal, setShowSalesWelcomeModal] = useState(false);
  const [salesUserId, setSalesUserId] = useState<number | null>(null);
  const [salesWelcomeMessage, setSalesWelcomeMessage] = useState<string>('Hey {FirstName}, welcome to Motionode!\n\nI\'m {Sales_person_Name}. I help new teams get their first project calibrated so you can start surfacing early risk signals.\n\nQuick question to point you in the right direction: Are you planning to use Motionode with a current project that already has an active deadline?');
  const [sendWelcomeMessage, setSendWelcomeMessage] = useState(true);
  const [showTemporaryPasswordModal, setShowTemporaryPasswordModal] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState<string>('');
  const [temporaryPasswordExpiresAt, setTemporaryPasswordExpiresAt] = useState<string>('');
  const [generateTemporaryPassword, { isLoading: isGeneratingTemporaryPassword }] = useGenerateTemporaryAccessPasswordMutation();

  // API calls
  const { data: usersData, isLoading, error, refetch } = useGetUsersQuery(filters);
  
  // Debug: Log user data to check is_sales_member and is_support_member values
  React.useEffect(() => {
    if (usersData?.data) {
      console.log('🔍 Users data:', usersData.data.map(u => ({
        id: u.id,
        name: u.name,
        is_support_member: u.is_support_member,
        is_sales_member: u.is_sales_member,
        types: {
          support: typeof u.is_support_member,
          sales: typeof u.is_sales_member
        }
      })));
    }
  }, [usersData]);
  const { data: userStats, isLoading: statsLoading } = useGetUserStatsQuery();
  const { data: searchResults } = useSearchUsersQuery(filters.search, {
    skip: !isSearchMode || !filters.search.trim()
  });
  const [updateUserStatus] = useUpdateUserStatusMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [resetUserPassword] = useResetUserPasswordMutation();
  const [updateUserEnterprisePlan] = useUpdateUserEnterprisePlanMutation();
  const [toggleMotionodeSupport] = useToggleMotionodeSupportMutation();
  const [toggleMotionodeSales] = useToggleMotionodeSalesMutation();
  const [toggleStripeTesting] = useToggleStripeTestingMutation();
  
  // Get verification URL when a user is selected
  const { data: verificationData, isLoading: isLoadingVerification, error: verificationError } = useGetVerificationUrlQuery(
    verificationUserId!,
    { skip: !verificationUserId }
  );
  const [getScopeReportLink, { isLoading: isLoadingScopeReportLink }] = useLazyGetScopeReportLinkQuery();
  const [getReshuffleReportLink, { isLoading: isLoadingReshuffleReportLink }] = useLazyGetReshuffleReportLinkQuery();
  const [getStartupReportLink, { isLoading: isLoadingStartupReportLink }] = useLazyGetStartupReportLinkQuery();
  const [getRiskIntelligenceReportLink, { isLoading: isLoadingRiskIntelligenceReportLink }] = useLazyGetRiskIntelligenceReportLinkQuery();
  const [getTrialResourcesLink, { isLoading: isLoadingTrialResourcesLink }] = useLazyGetTrialResourcesLinkQuery();
  const [completeScopeReportEmail, { isLoading: isLoadingCompleteEmail }] = useCompleteScopeReportEmailVerificationMutation();

  // Handle complete scope report email verification (manually update user email)
  const handleCompleteScopeReportEmail = async (userId: number) => {
    try {
      const result = await completeScopeReportEmail(userId).unwrap();
      alert(`User email updated to ${result.email}`);
      refetch(); // Refresh the user list to show updated email
    } catch (e: any) {
      alert(e?.data?.message || e?.message || 'Failed to update email');
    }
  };

  // Get users data: main list uses API with exclude_ghost (20 per page); search mode filters client-side for consistency
  // Hide users whose email STARTS with "ghost" (like ghost-xxx@placeholder.local), but keep scope-test users (scope-test-xxx@ghost.motionode.com)
  const currentUsers = useMemo(() => {
    const hideGhost = (list: any[]) => (list || []).filter((u: any) => {
      const email = (u.email || '').toLowerCase();
      return !email.startsWith('ghost');
    });
    if (isSearchMode && searchResults) {
      const data = hideGhost(searchResults);
      return { data, meta: { page: 1, limit: data.length, total: data.length, totalPages: 1 } };
    }
    const raw = usersData || { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
    return { data: raw.data, meta: raw.meta };
  }, [isSearchMode, searchResults, usersData]);

  // Handle search
  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setIsSearchMode(value.trim().length > 0);
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof UserFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    setIsSearchMode(false);
  };

  // Handle sorting
  const handleSort = (sortBy: string, sortOrder: 'ASC' | 'DESC') => {
    setFilters(prev => ({ ...prev, sortBy, sortOrder, page: 1 }));
    setIsSearchMode(false);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };


  // Handle user deletion
  const handleDeleteUser = async (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(userId);
      } catch (error) {
        console.error('Failed to delete user:', error);
      }
    }
  };

  // Handle Motionode Support toggle
  const handleToggleSupport = async (userId: number) => {
    try {
      await toggleMotionodeSupport(userId).unwrap();
      alert('Motionode Support role toggled successfully');
      refetch(); // Refresh the user list to show updated status
    } catch (error: any) {
      console.error('Failed to toggle Motionode Support:', error);
      alert(error?.data?.message || error?.message || 'Failed to toggle Motionode Support role');
    }
  };

  // Handle generate temporary access password
  const handleGenerateTemporaryPassword = async (userId: number) => {
    setTemporaryPassword('');
    setTemporaryPasswordExpiresAt('');
    setShowTemporaryPasswordModal(true);
    
    try {
      const result = await generateTemporaryPassword(userId).unwrap();
      setTemporaryPassword(result.password);
      setTemporaryPasswordExpiresAt(result.expiresAt);
    } catch (error: any) {
      console.error('Failed to generate temporary password:', error);
      alert(error?.data?.message || error?.message || 'Failed to generate temporary password');
      setShowTemporaryPasswordModal(false);
    }
  };

  // Handle close temporary password modal
  const handleCloseTemporaryPasswordModal = () => {
    setShowTemporaryPasswordModal(false);
    setTemporaryPassword('');
    setTemporaryPasswordExpiresAt('');
  };

  // Handle copy temporary password
  const handleCopyTemporaryPassword = () => {
    if (temporaryPassword) {
      navigator.clipboard.writeText(temporaryPassword);
      alert('Password copied to clipboard!');
    }
  };

  // Handle Stripe testing toggle
  const handleToggleStripeTesting = async (userId: number) => {
    try {
      await toggleStripeTesting(userId).unwrap();
      alert('Stripe testing mode toggled successfully');
      refetch(); // Refresh the user list to show updated status
    } catch (error: any) {
      console.error('Failed to toggle Stripe testing:', error);
      alert(error?.data?.message || error?.message || 'Failed to toggle Stripe testing mode');
    }
  };

  // Handle Motionode Sales toggle
  const handleToggleSales = async (userId: number) => {
    // Check if user is currently a sales member
    const user = usersData?.data.find((u: any) => u.id === userId) || searchResults?.find((u: any) => u.id === userId);
    const isCurrentlySales = user?.is_sales_member === 1;
    
    if (isCurrentlySales) {
      // If already sales, just toggle off
      try {
        await toggleMotionodeSales({ id: userId, welcomeMessage: null }).unwrap();
        alert('Motionode Sales role removed successfully');
        refetch();
      } catch (error: any) {
        console.error('Failed to toggle Motionode Sales:', error);
        alert(error?.data?.message || error?.message || 'Failed to toggle Motionode Sales role');
      }
    } else {
      // If not sales, show modal to configure welcome message
      setSalesUserId(userId);
      setShowSalesWelcomeModal(true);
    }
  };
  
  // Confirm sales toggle with welcome message
  const confirmSalesToggle = async () => {
    if (!salesUserId) return;
    
    try {
      const messageToSend = sendWelcomeMessage ? salesWelcomeMessage : null;
      await toggleMotionodeSales({ id: salesUserId, welcomeMessage: messageToSend }).unwrap();
      alert('Motionode Sales role set successfully');
      refetch();
      setShowSalesWelcomeModal(false);
      setSalesUserId(null);
    } catch (error: any) {
      console.error('Failed to toggle Motionode Sales:', error);
      alert(error?.data?.message || error?.message || 'Failed to toggle Motionode Sales role');
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action: 'activate' | 'deactivate' | 'delete') => {
    if (selectedUsers.length === 0) return;

    if (action === 'delete') {
      if (window.confirm(`Are you sure you want to delete ${selectedUsers.length} user(s)?`)) {
        for (const userId of selectedUsers) {
          try {
            await deleteUser(userId);
          } catch (error) {
            console.error(`Failed to delete user ${userId}:`, error);
          }
        }
      }
    } else {
      const isConnected = action === 'activate';
      for (const userId of selectedUsers) {
        try {
          await updateUserStatus({ id: userId, isConnected });
        } catch (error) {
          console.error(`Failed to ${action} user ${userId}:`, error);
        }
      }
    }
    setSelectedUsers([]);
  };

  // Format date in Eastern Time (server sends UTC without timezone)
  const formatDate = (dateString: string) => formatDateTimeEST(dateString);

  // Handle get verification URL
  const handleGetVerificationUrl = (userId: number) => {
    setVerificationUserId(userId);
  };

  // Handle close verification modal
  const handleCloseVerificationModal = () => {
    setVerificationUserId(null);
  };

  // Handle copy verification URL
  const handleCopyVerificationUrl = () => {
    if (verificationData?.verificationUrl) {
      navigator.clipboard.writeText(verificationData.verificationUrl);
      alert('Verification URL copied to clipboard!');
    }
  };

  // Handle get scope creep report link (simulate email link)
  const handleGetScopeReportLink = async (userId: number, flow?: string) => {
    try {
      const result = await getScopeReportLink(userId).unwrap();
      if (result?.url) {
        navigator.clipboard.writeText(result.url);
        const label = flow === 'check'
          ? 'Mo link'
          : flow === 'motiocomp'
          ? 'Motiocomp link'
          : flow === 'plan'
          ? 'Plan report link'
          : flow === 'trial'
          ? 'Trial report link'
          : 'Scope creep report link';
        alert(`${label} copied to clipboard. Open it in a browser to simulate the email link.`);
      }
    } catch (e: any) {
      alert(e?.data?.message || e?.message || 'Failed to get report link');
    }
  };

  // Handle get reshuffle report link (same as email link for reshuffle flow – reshuffle-report path)
  const handleGetReshuffleReportLink = async (userId: number) => {
    try {
      const result = await getReshuffleReportLink(userId).unwrap();
      if (result?.url) {
        navigator.clipboard.writeText(result.url);
        alert('Reshuffle report link copied to clipboard. Open it in a browser to simulate the email link.');
      }
    } catch (e: any) {
      alert(e?.data?.message || e?.message || 'Failed to get reshuffle report link');
    }
  };

  // Handle get startup report link (simulate email link for /startup flow)
  const handleGetStartupReportLink = async (userId: number) => {
    try {
      const result = await getStartupReportLink(userId).unwrap();
      if (result?.url) {
        navigator.clipboard.writeText(result.url);
        alert('Startup report link copied to clipboard. Open it in a browser to simulate the email link.');
      }
    } catch (e: any) {
      alert(e?.data?.message || e?.message || 'Failed to get startup report link');
    }
  };

  // Handle get risk intelligence report link (simulate email link for /riskintelligence flow)
  const handleGetRiskIntelligenceReportLink = async (userId: number) => {
    try {
      const result = await getRiskIntelligenceReportLink(userId).unwrap();
      if (result?.url) {
        navigator.clipboard.writeText(result.url);
        alert('Risk intelligence report link copied to clipboard. Open it in a browser to simulate the email link.');
      }
    } catch (e: any) {
      alert(e?.data?.message || e?.message || 'Failed to get risk intelligence report link');
    }
  };

  // Handle get trial resources link (simulate the resources email link)
  const handleGetTrialResourcesLink = async (userId: number) => {
    try {
      const result = await getTrialResourcesLink(userId).unwrap();
      if (result?.url) {
        navigator.clipboard.writeText(result.url);
        alert('Trial resources link copied to clipboard. Open it in a browser to simulate the resources email link.');
      }
    } catch (e: any) {
      alert(e?.data?.message || e?.message || 'Failed to get trial resources link');
    }
  };

  // Handle reset password
  const handleResetPassword = (userId: number) => {
    setResetPasswordUserId(userId);
    setResetPasswordMode('random');
    setCustomPassword('');
    // Auto-generate a random password when opening the modal
    const randomPwd = generateRandomPassword();
    setGeneratedPassword(randomPwd);
  };

  // Generate random password
  const generateRandomPassword = () => {
    const length = 12;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '!@#$%^&*';
    const allChars = uppercase + lowercase + numbers + special;
    
    let password = '';
    // Ensure at least one of each type
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    setGeneratedPassword(password);
    return password;
  };

  // Handle confirm reset password
  const handleConfirmResetPassword = async () => {
    if (!resetPasswordUserId) return;
    
    let passwordToUse = '';
    if (resetPasswordMode === 'random') {
      if (!generatedPassword) {
        passwordToUse = generateRandomPassword();
      } else {
        passwordToUse = generatedPassword;
      }
    } else {
      if (!customPassword || customPassword.length < 6) {
        alert('Password must be at least 6 characters long');
        return;
      }
      passwordToUse = customPassword;
    }

    try {
      console.log('Resetting password for user ID:', resetPasswordUserId);
      const response = await resetUserPassword({ id: resetPasswordUserId, password: passwordToUse }).unwrap();
      const user = currentUsers?.data?.find((u: any) => u.id === resetPasswordUserId);
      const isGoogleUser = user?.auth_provider === 'GOOGLE';
      
      let message = `Password reset successfully!\n\nNew password: ${passwordToUse}`;
      if (isGoogleUser || response?.wasGoogleUser) {
        message += `\n\n⚠️ Note: This user was previously using Google authentication. They can now login with email and password using their email address and this new password.`;
      }
      
      alert(message);
      setResetPasswordUserId(null);
      setCustomPassword('');
      setGeneratedPassword('');
    } catch (error: any) {
      console.error('Failed to reset password:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to reset password';
      alert(errorMessage);
    }
  };

  // Handle close reset password modal
  const handleCloseResetPasswordModal = () => {
    setResetPasswordUserId(null);
    setCustomPassword('');
    setGeneratedPassword('');
    setResetPasswordMode('random');
  };

  // Handle open enterprise plan modal
  const handleOpenEnterprisePlan = (userId: number) => {
    setEnterpriseUserId(userId);
    setSeatsValue('');
  };

  // Handle close enterprise plan modal
  const handleCloseEnterprisePlan = () => {
    setEnterpriseUserId(null);
    setSeatsValue('');
  };

  // Handle update enterprise plan
  const handleUpdateEnterprisePlan = async () => {
    if (!enterpriseUserId) return;

    try {
      const seats = seatsValue.trim() ? parseInt(seatsValue) : undefined;
      if (seats !== undefined && (isNaN(seats) || seats < 0)) {
        alert('The number of seats must be a valid number greater than or equal to 0');
        return;
      }

      await updateUserEnterprisePlan({ id: enterpriseUserId, seats }).unwrap();
      // The backend will automatically switch between Enterprise (2) and Normal (4)
      alert('Plan updated successfully');
      handleCloseEnterprisePlan();
    } catch (error: any) {
      console.error('Failed to update enterprise plan:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to update enterprise plan';
      alert(errorMessage);
    }
  };

  if (isLoading || statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600">
        <p>Error loading users: {error.toString()}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
        <p className="text-gray-600 mt-2">Manage and monitor all users in the system</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Users"
          value={userStats?.totalUsers || 0}
          subtitle="All registered users"
          trend="+12%"
          trendUp={true}
        />
        <StatsCard
          title="Active Users"
          value={userStats?.activeUsers || 0}
          subtitle="Currently active"
          trend="+8%"
          trendUp={true}
        />
        <StatsCard
          title="Inactive Users"
          value={userStats?.inactiveUsers || 0}
          subtitle="Not active"
          trend="-3%"
          trendUp={false}
        />
        <StatsCard
          title="New This Month"
          value={userStats?.newUsersThisMonth || 0}
          subtitle="New registrations"
          trend="+15%"
          trendUp={true}
        />
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search users..."
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-3">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="created_at">Created Date</option>
              <option value="email">Email</option>
              <option value="name">Name</option>
            </select>

            <select
              value={filters.sortOrder}
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="DESC">Descending</option>
              <option value="ASC">Ascending</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => handleBulkAction('activate')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Activate ({selectedUsers.length})
            </button>
            <button
              onClick={() => handleBulkAction('deactivate')}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              Deactivate ({selectedUsers.length})
            </button>
            <button
              onClick={() => handleBulkAction('delete')}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete ({selectedUsers.length})
            </button>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === currentUsers.data.length && currentUsers.data.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(currentUsers.data.map(user => user.id));
                      } else {
                        setSelectedUsers([]);
                      }
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account ID
                </th>
                <SortableTableHeader
                  label="User"
                  sortKey="name"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Email"
                  sortKey="email"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purchased Seats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Used Seats
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credits Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organizations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Industry
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <SortableTableHeader
                  label="Created"
                  sortKey="createdAt"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentUsers.data.map((user) => (
                <UserTableRow 
                  key={user.id} 
                  user={user} 
                  selectedUsers={selectedUsers}
                  setSelectedUsers={setSelectedUsers}
                  handleGetVerificationUrl={handleGetVerificationUrl}
                  handleResetPassword={handleResetPassword}
                  handleDeleteUser={handleDeleteUser}
                  handleOpenEnterprisePlan={handleOpenEnterprisePlan}
                  formatDate={formatDate}
                  onEditSeats={(userId: number) => setEditSeatsUserId(userId)}
                  handleToggleSupport={handleToggleSupport}
                  handleToggleSales={handleToggleSales}
                  handleToggleStripeTesting={handleToggleStripeTesting}
                  handleGenerateTemporaryPassword={handleGenerateTemporaryPassword}
                  handleGetScopeReportLink={handleGetScopeReportLink}
                  isLoadingScopeReportLink={isLoadingScopeReportLink}
                  handleGetReshuffleReportLink={handleGetReshuffleReportLink}
                  isLoadingReshuffleReportLink={isLoadingReshuffleReportLink}
                  handleGetStartupReportLink={handleGetStartupReportLink}
                  isLoadingStartupReportLink={isLoadingStartupReportLink}
                  handleGetRiskIntelligenceReportLink={handleGetRiskIntelligenceReportLink}
                  isLoadingRiskIntelligenceReportLink={isLoadingRiskIntelligenceReportLink}
                  handleGetTrialResourcesLink={handleGetTrialResourcesLink}
                  isLoadingTrialResourcesLink={isLoadingTrialResourcesLink}
                  handleCompleteScopeReportEmail={handleCompleteScopeReportEmail}
                  isLoadingCompleteEmail={isLoadingCompleteEmail}
                />
              ))}
            </tbody>
          </table>

        {/* Pagination */}
        {currentUsers.meta.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(Math.max(1, currentUsers.meta.page - 1))}
                disabled={currentUsers.meta.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(Math.min(currentUsers.meta.totalPages, currentUsers.meta.page + 1))}
                disabled={currentUsers.meta.page === currentUsers.meta.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {((currentUsers.meta.page - 1) * currentUsers.meta.limit) + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentUsers.meta.page * currentUsers.meta.limit, currentUsers.meta.total)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{currentUsers.meta.total}</span>{' '}
                  results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentUsers.meta.page - 1))}
                    disabled={currentUsers.meta.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(5, currentUsers.meta.totalPages))].map((_, index) => {
                    const pageNum = Math.max(1, Math.min(currentUsers.meta.totalPages - 4, currentUsers.meta.page - 2)) + index;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentUsers.meta.page
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(Math.min(currentUsers.meta.totalPages, currentUsers.meta.page + 1))}
                    disabled={currentUsers.meta.page === currentUsers.meta.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Verification URL Modal */}
      {verificationUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Verification Email URL</h2>
              <button
                onClick={handleCloseVerificationModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {isLoadingVerification ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : verificationError ? (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800 font-medium mb-2">Error getting verification URL:</p>
                  <p className="text-sm text-red-600">
                    {verificationError && 'data' in verificationError && typeof verificationError.data === 'object' && verificationError.data !== null && 'message' in verificationError.data
                      ? String(verificationError.data.message)
                      : verificationError && 'error' in verificationError
                      ? String(verificationError.error)
                      : 'Failed to get verification URL. Please try again.'}
                  </p>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleCloseVerificationModal}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : verificationData?.verificationUrl ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification URL:
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      readOnly
                      value={verificationData.verificationUrl}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                    />
                    <button
                      onClick={handleCopyVerificationUrl}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Copy</span>
                    </button>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> You can use this URL to verify the user's email without needing to access their email inbox.
                  </p>
                </div>
                <div className="flex justify-end">
                  <a
                    href={verificationData.verificationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Open in New Tab
                  </a>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-red-600">
                Failed to get verification URL. Please try again.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
              <button
                onClick={handleCloseResetPasswordModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password Type:
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="passwordMode"
                      value="random"
                      checked={resetPasswordMode === 'random'}
                      onChange={() => {
                        setResetPasswordMode('random');
                        setGeneratedPassword('');
                      }}
                      className="mr-2"
                    />
                    <span>Random Password</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="passwordMode"
                      value="custom"
                      checked={resetPasswordMode === 'custom'}
                      onChange={() => setResetPasswordMode('custom')}
                      className="mr-2"
                    />
                    <span>Custom Password</span>
                  </label>
                </div>
              </div>

              {resetPasswordMode === 'random' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Generated Password:
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      readOnly
                      value={generatedPassword || 'Click Generate to create a password'}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                    />
                    <button
                      onClick={() => generateRandomPassword()}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      Generate
                    </button>
                  </div>
                  {generatedPassword && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(generatedPassword);
                        alert('Password copied to clipboard!');
                      }}
                      className="mt-2 text-sm text-primary-600 hover:text-primary-700"
                    >
                      Copy Password
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Password:
                  </label>
                  <input
                    type="password"
                    value={customPassword}
                    onChange={(e) => setCustomPassword(e.target.value)}
                    placeholder="Enter custom password (min 6 characters)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {customPassword && customPassword.length < 6 && (
                    <p className="mt-1 text-sm text-red-600">Password must be at least 6 characters long</p>
                  )}
                </div>
              )}

              {(() => {
                const user = currentUsers?.data?.find((u: any) => u.id === resetPasswordUserId);
                const isGoogleUser = user?.auth_provider === 'GOOGLE';
                
                return (
                  <>
                    {isGoogleUser && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <strong>ℹ️ Note:</strong> This user is currently using Google authentication. After resetting the password, they will be able to login with their email address and the new password instead.
                        </p>
                      </div>
                    )}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        <strong>Warning:</strong> The new password will be displayed after reset. Make sure to save it securely.
                      </p>
                    </div>
                  </>
                );
              })()}

              <div className="flex justify-end gap-2">
                <button
                  onClick={handleCloseResetPasswordModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmResetPassword}
                  disabled={resetPasswordMode === 'random' && !generatedPassword || resetPasswordMode === 'custom' && (!customPassword || customPassword.length < 6)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Welcome Message Modal */}
      {showSalesWelcomeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Set as Motionode Sales</h2>
              <button
                onClick={() => {
                  setShowSalesWelcomeModal(false);
                  setSalesUserId(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Info:</strong> This user will automatically send a welcome message to new users when they create their first project in their first organization.
                </p>
              </div>

              <div>
                <label className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    checked={sendWelcomeMessage}
                    onChange={(e) => setSendWelcomeMessage(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Send welcome message to new users</span>
                </label>
              </div>

              {sendWelcomeMessage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Welcome Message:
                    <span className="ml-2 text-xs text-gray-500">
                      Use {'{FirstName}'} for user's first name and {'{Sales_person_Name}'} for sales person's name
                    </span>
                  </label>
                  <textarea
                    value={salesWelcomeMessage}
                    onChange={(e) => setSalesWelcomeMessage(e.target.value)}
                    rows={8}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                    placeholder="Enter the welcome message..."
                  />
                  <div className="mt-2 text-xs text-gray-500">
                    <p>Available placeholders:</p>
                    <ul className="list-disc ml-5">
                      <li><code>{'{FirstName}'}</code> - Will be replaced with the user's first name</li>
                      <li><code>{'{Sales_person_Name}'}</code> - Will be replaced with the sales person's name</li>
                    </ul>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowSalesWelcomeModal(false);
                    setSalesUserId(null);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSalesToggle}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Confirm & Set as Sales
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enterprise Plan Modal */}
      {enterpriseUserId && (
        <EnterprisePlanModal
          userId={enterpriseUserId}
          seatsValue={seatsValue}
          setSeatsValue={setSeatsValue}
          onClose={handleCloseEnterprisePlan}
          onUpdate={handleUpdateEnterprisePlan}
        />
      )}

      {/* Edit Seats Modal */}
      {editSeatsUserId && (
        <EditSeatsModal
          userId={editSeatsUserId}
          onClose={() => setEditSeatsUserId(null)}
        />
      )}

      {/* Temporary Access Password Modal */}
      {showTemporaryPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Temporary Access Password</h2>
              <button
                onClick={handleCloseTemporaryPasswordModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {isGeneratingTemporaryPassword ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : temporaryPassword ? (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 font-medium mb-2">
                    ⚠️ Important: This password expires in 5 minutes
                  </p>
                  <p className="text-sm text-yellow-700">
                    Use the user's email address and this temporary password to log in to their account.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temporary Password:
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      readOnly
                      value={temporaryPassword}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                    />
                    <button
                      onClick={handleCopyTemporaryPassword}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Copy</span>
                    </button>
                  </div>
                </div>

                {temporaryPasswordExpiresAt && (
                  <div className="text-sm text-gray-600">
                    <p>Expires at: {formatDateTimeEST(temporaryPasswordExpiresAt)}</p>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>How to use:</strong>
                  </p>
                  <ol className="text-sm text-blue-700 mt-2 list-decimal list-inside space-y-1">
                    <li>Go to the login page</li>
                    <li>Enter the user's email address</li>
                    <li>Enter this temporary password</li>
                    <li>You will be logged in as that user</li>
                  </ol>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleCloseTemporaryPasswordModal}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-red-600">
                Failed to generate temporary password. Please try again.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Plan Status Button Component
interface PlanStatusButtonProps {
  userId: number;
  onOpenModal: (userId: number) => void;
  user?: any; // User object with inline data (optional for backward compatibility)
}

const PlanStatusButton: React.FC<PlanStatusButtonProps> = ({ userId, onOpenModal, user }) => {
  // Use inline data if available, otherwise fallback to query
  const accountInfo = user?.account_info;
  
  // Fallback to query only if inline data is not available
  const { data: accountInfoQuery } = useGetUserAccountInfoQuery(userId, {
    skip: !userId || !!accountInfo,
    refetchOnMountOrArgChange: false,
  });

  const finalAccountInfo = accountInfo || accountInfoQuery;
  const isEnterprise = finalAccountInfo?.subscription_id === 2;
  const isNormal = finalAccountInfo?.subscription_id === 4;

  return (
    <button
      onClick={() => onOpenModal(userId)}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
        isEnterprise
          ? 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
          : isNormal
          ? 'bg-gray-100 text-gray-800 hover:bg-gray-200'
          : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
      }`}
      title={isEnterprise ? 'Change to Normal Plan' : 'Change to Enterprise Plan'}
    >
      {isEnterprise ? 'Enterprise → Normal' : isNormal ? 'Normal → Enterprise' : 'Assign Plan'}
    </button>
  );
};

// Enterprise Plan Modal Component
interface EnterprisePlanModalProps {
  userId: number;
  seatsValue: string;
  setSeatsValue: (value: string) => void;
  onClose: () => void;
  onUpdate: () => void;
}

const EnterprisePlanModal: React.FC<EnterprisePlanModalProps> = ({
  userId,
  seatsValue,
  setSeatsValue,
  onClose,
  onUpdate
}) => {
  const { data: accountInfo, isLoading: isLoadingAccountInfo } = useGetUserAccountInfoQuery(userId, {
    skip: !userId
  });

  const isEnterprise = accountInfo?.subscription_id === 2;
  const isNormal = accountInfo?.subscription_id === 4;

  // Set initial seats value when account info loads
  useEffect(() => {
    if (accountInfo?.seats !== null && accountInfo?.seats !== undefined && !seatsValue) {
      setSeatsValue(accountInfo.seats.toString());
    }
  }, [accountInfo, seatsValue, setSeatsValue]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {isEnterprise ? 'Change to Normal Plan' : 'Assign Enterprise Plan'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {isLoadingAccountInfo ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-700 mb-4">
                {isEnterprise 
                  ? `This user has the Enterprise plan (subscription_id = 2). It will be changed to the Normal plan (subscription_id = 4). You can update the number of seats before changing.`
                  : isNormal
                  ? `This user has the Normal plan (subscription_id = 4). It will be changed to the Enterprise plan (subscription_id = 2). You can assign the number of seats.`
                  : 'Assign the Enterprise plan to this user. This will change the subscription_id to 2 in data_accounts.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Seats:
              </label>
              <input
                type="number"
                min="0"
                value={seatsValue}
                onChange={(e) => setSeatsValue(e.target.value)}
                placeholder={accountInfo?.seats !== null && accountInfo?.seats !== undefined 
                  ? `Current: ${accountInfo.seats}` 
                  : 'Enter the number of seats'}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {accountInfo?.seats !== null && accountInfo?.seats !== undefined && (
                <p className="mt-1 text-xs text-gray-500">
                  Current seats: {accountInfo.seats}
                </p>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> {isEnterprise 
                  ? `The subscription_id will change from 2 (Enterprise) to 4 (Normal) in data_accounts.`
                  : isNormal
                  ? `The subscription_id will change from 4 (Normal) to 2 (Enterprise) in data_accounts.`
                  : 'When assigning the Enterprise plan, the subscription_id field will be updated to 2 in the user\'s data_accounts table.'}
                {seatsValue && seatsValue.trim() && (
                  <span className="block mt-1">A record will be created in data_account_movements with seats_count = {seatsValue}.</span>
                )}
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={onUpdate}
                className={`px-4 py-2 text-white rounded-lg ${
                  isEnterprise 
                    ? 'bg-gray-600 hover:bg-gray-700' 
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {isEnterprise ? 'Change to Normal Plan' : isNormal ? 'Change to Enterprise Plan' : 'Assign Enterprise Plan'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Account Status Cell Component - Shows account type (Free, Paid Enterprise, Paid Self-serve, On hold)
interface AccountStatusCellProps {
  userId: number;
}

const AccountStatusCell: React.FC<AccountStatusCellProps> = ({ userId }) => {
  const { data: accountInfo } = useGetUserAccountInfoQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: false, // Use cache if available
  });
  const { data: seatInfo } = useGetUserSeatInfoQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: false, // Use cache if available
  });

  const getAccountType = () => {
    if (!accountInfo) return 'Free';
    
    // Check if on hold
    if (accountInfo.status === 'ON_HOLD' || accountInfo.on_hold_at) {
      return 'On hold';
    }
    
    // Check purchased seats - if 0, it's Free regardless of subscription_id
    const purchasedSeats = seatInfo?.purchasedSeats || 0;
    if (purchasedSeats === 0) {
      return 'Free';
    }
    
    // Check subscription_id only if they have purchased seats
    if (accountInfo.subscription_id === 2) {
      return 'Paid (Enterprise)';
    }
    if (accountInfo.subscription_id === 4) {
      return 'Paid (Self-serve)';
    }
    
    return 'Free';
  };

  const accountType = getAccountType();
  
  const getStatusColor = () => {
    switch (accountType) {
      case 'Paid (Enterprise)':
        return 'bg-indigo-100 text-indigo-800';
      case 'Paid (Self-serve)':
        return 'bg-gray-100 text-gray-800';
      case 'On hold':
        return 'bg-red-100 text-red-800';
      case 'Free':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor()}`}>
      {accountType}
    </span>
  );
};

// Account Status Button Component - Replaces Activate/Deactivate
interface AccountStatusButtonProps {
  user: any; // User object with inline data
}

const AccountStatusButton: React.FC<AccountStatusButtonProps> = ({ user }) => {
  // Use inline data if available, otherwise fallback to query
  const accountInfo = user.account_info;
  const seatInfo = user.seat_info;
  
  // Fallback to queries only if inline data is not available
  const { data: accountInfoQuery, refetch } = useGetUserAccountInfoQuery(user.id, {
    skip: !user.id || !!accountInfo,
    refetchOnMountOrArgChange: false,
  });
  const { data: seatInfoQuery } = useGetUserSeatInfoQuery(user.id, {
    skip: !user.id || !!seatInfo,
    refetchOnMountOrArgChange: false,
  });

  const finalAccountInfo = accountInfo || accountInfoQuery;
  const finalSeatInfo = seatInfo || seatInfoQuery;
  const [switchAccountStatus] = useSwitchAccountStatusMutation();

  const getAccountType = () => {
    if (!finalAccountInfo) return 'FREE';
    
    // Check if on hold
    if (finalAccountInfo.status === 'ON_HOLD' || finalAccountInfo.on_hold_at) {
      return 'ON_HOLD';
    }
    
    // Check purchased seats - if 0, it's Free regardless of subscription_id
    const purchasedSeats = finalSeatInfo?.purchasedSeats || 0;
    if (purchasedSeats === 0) {
      return 'FREE';
    }
    
    // Check subscription_id only if they have purchased seats
    if (finalAccountInfo.subscription_id === 2) {
      return 'ENTERPRISE';
    }
    if (finalAccountInfo.subscription_id === 4) {
      return 'SELF_SERVE';
    }
    
    return 'FREE';
  };

  const currentType = getAccountType();

  const handleStatusSwitch = async (targetStatus: 'FREE' | 'ENTERPRISE' | 'SELF_SERVE' | 'ON_HOLD') => {
    let reason: string | undefined;
    
    if (targetStatus === 'ON_HOLD') {
      const reasonInput = prompt('Enter reason for putting account on hold:');
      if (!reasonInput || reasonInput.trim().length === 0) {
        alert('A reason is required when putting an account on hold.');
        return;
      }
      reason = reasonInput;
    } else {
      const confirmMessage = 
        targetStatus === 'ENTERPRISE' ? 'Switch to Paid (Enterprise)?' :
        targetStatus === 'SELF_SERVE' ? 'Switch to Paid (Self-serve)?' :
        'Switch to Free?';
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    try {
      await switchAccountStatus({ 
        id: user.id, 
        status: targetStatus,
        reason 
      }).unwrap();
      refetch();
    } catch (error: any) {
      console.error('Failed to switch account status:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to switch account status';
      alert(errorMessage);
    }
  };

  // Determine which buttons to show based on current status
  const getButtons = () => {
    switch (currentType) {
      case 'ON_HOLD':
        return (
          <>
            <button
              onClick={() => handleStatusSwitch('ENTERPRISE')}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
              title="Switch to Paid (Enterprise)"
            >
              On hold → Paid (Enterprise)
            </button>
            <button
              onClick={() => handleStatusSwitch('SELF_SERVE')}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
              title="Switch to Paid (Self-serve)"
            >
              On hold → Paid (Self-serve)
            </button>
          </>
        );
      case 'ENTERPRISE':
        return (
          <button
            onClick={() => handleStatusSwitch('ON_HOLD')}
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200"
            title="Put account on hold"
          >
            Paid (Enterprise) → On hold
          </button>
        );
      case 'SELF_SERVE':
        return (
          <button
            onClick={() => handleStatusSwitch('ON_HOLD')}
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200"
            title="Put account on hold"
          >
            Paid (Self-serve) → On hold
          </button>
        );
      case 'FREE':
      default:
        return null; // Free accounts don't need status switching buttons
    }
  };

  const buttons = getButtons();
  
  if (!buttons) {
    return null;
  }

  return <div className="flex flex-wrap gap-2">{buttons}</div>;
};

// Organizations Cell Component - Now uses inline data
interface OrganizationsCellProps {
  user: any; // User object with inline data
}

const formatCredits = (value: number) => Number(value || 0).toLocaleString(undefined, {
  maximumFractionDigits: Number(value || 0) % 1 === 0 ? 0 : 1,
});

const formatCreditMonthLabel = (ym: string) => {
  const [year, month] = ym.split('-').map(Number);
  if (!year || !month) return ym;
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

interface CreditsUsedCellProps {
  user: any;
}

const CreditsUsedCell: React.FC<CreditsUsedCellProps> = ({ user }) => {
  const credits = user.credits_info;
  const [showTooltip, setShowTooltip] = useState(false);

  if (!credits || (credits.totalUsed === 0 && (!credits.byMonth || credits.byMonth.length === 0))) {
    return <span className="text-sm text-gray-400">0</span>;
  }

  const hasBreakdown = credits.byMonth && credits.byMonth.length > 0;

  return (
    <div className="relative inline-block">
      <span
        className={`text-sm text-gray-900 ${hasBreakdown ? 'cursor-help underline decoration-dotted' : ''}`}
        onMouseEnter={() => hasBreakdown && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {formatCredits(credits.totalUsed)}
      </span>
      {showTooltip && hasBreakdown && (
        <div className="absolute z-50 left-0 mt-2 w-56 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg">
          <div className="font-semibold mb-2">Credits by month</div>
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            {credits.byMonth.map((entry: { month: string; credits: number }) => (
              <li key={entry.month} className="flex justify-between gap-3">
                <span>{formatCreditMonthLabel(entry.month)}</span>
                <span className="font-medium tabular-nums">{formatCredits(entry.credits)}</span>
              </li>
            ))}
          </ul>
          <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45" />
        </div>
      )}
    </div>
  );
};

const OrganizationsCell: React.FC<OrganizationsCellProps> = ({ user }) => {
  // Use inline data if available, otherwise fallback to query
  const organizations = user.organizations_info;
  
  // Fallback to query only if inline data is not available
  const { data: organizationsQuery, isLoading } = useGetUserOrganizationsQuery(user.id, {
    skip: !user.id || !!organizations,
    refetchOnMountOrArgChange: false,
  });
  
  const [showTooltip, setShowTooltip] = useState(false);

  const finalOrganizations = organizations || organizationsQuery;

  if (isLoading && !finalOrganizations) {
    return <span className="text-gray-400">...</span>;
  }

  if (!finalOrganizations || finalOrganizations.count === 0) {
    return <span className="text-sm text-gray-400">0</span>;
  }

  return (
    <div className="relative inline-block">
      <span
        className="text-sm text-gray-900 cursor-help underline decoration-dotted"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {finalOrganizations.count}
      </span>
      {showTooltip && finalOrganizations.names && finalOrganizations.names.length > 0 && (
        <div className="absolute z-50 left-0 mt-2 w-64 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg">
          <div className="font-semibold mb-2">Organizations ({finalOrganizations.count}):</div>
          <ul className="space-y-1">
            {finalOrganizations.names.map((name: string, index: number) => (
              <li key={index} className="truncate">• {name}</li>
            ))}
          </ul>
          <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
        </div>
      )}
    </div>
  );
};

// User Table Row Component
interface UserTableRowProps {
  user: any;
  selectedUsers: number[];
  setSelectedUsers: React.Dispatch<React.SetStateAction<number[]>>;
  handleGetVerificationUrl: (userId: number) => void;
  handleResetPassword: (userId: number) => void;
  handleDeleteUser: (userId: number) => void;
  handleOpenEnterprisePlan: (userId: number) => void;
  formatDate: (date: string) => string;
  onEditSeats: (userId: number) => void;
  handleToggleSupport: (userId: number) => void;
  handleToggleSales: (userId: number) => void;
  handleToggleStripeTesting: (userId: number) => void;
  handleGenerateTemporaryPassword: (userId: number) => void;
  handleGetScopeReportLink: (userId: number, flow?: string) => void;
  isLoadingScopeReportLink?: boolean;
  handleGetReshuffleReportLink: (userId: number) => void;
  isLoadingReshuffleReportLink?: boolean;
  handleGetStartupReportLink: (userId: number) => void;
  isLoadingStartupReportLink?: boolean;
  handleGetRiskIntelligenceReportLink: (userId: number) => void;
  isLoadingRiskIntelligenceReportLink?: boolean;
  handleGetTrialResourcesLink: (userId: number) => void;
  isLoadingTrialResourcesLink?: boolean;
  handleCompleteScopeReportEmail: (userId: number) => void;
  isLoadingCompleteEmail?: boolean;
}

const UserTableRow: React.FC<UserTableRowProps> = ({
  user,
  selectedUsers,
  setSelectedUsers,
  handleGetVerificationUrl,
  handleResetPassword,
  handleDeleteUser,
  handleOpenEnterprisePlan,
  formatDate,
  onEditSeats,
  handleToggleSupport,
  handleToggleSales,
  handleToggleStripeTesting,
  handleGenerateTemporaryPassword,
  handleGetScopeReportLink,
  isLoadingScopeReportLink,
  handleGetReshuffleReportLink,
  isLoadingReshuffleReportLink,
  handleGetStartupReportLink,
  isLoadingStartupReportLink,
  handleGetRiskIntelligenceReportLink,
  isLoadingRiskIntelligenceReportLink,
  handleGetTrialResourcesLink,
  isLoadingTrialResourcesLink,
  handleCompleteScopeReportEmail,
  isLoadingCompleteEmail
}) => {
  // Check for pending scope report, startup report, risk intelligence report, and trial resources email verification
  const { data: pendingScopeEmail } = useGetPendingScopeReportEmailQuery(user.id, {
    skip: !user.id
  });
  const { data: pendingStartupEmail } = useGetPendingStartupReportEmailQuery(user.id, {
    skip: !user.id
  });
  const { data: pendingRiskintelligenceEmail } = useGetPendingRiskIntelligenceReportEmailQuery(user.id, {
    skip: !user.id
  });
  const { data: pendingTrialResourcesEmail } = useGetPendingTrialResourcesEmailQuery(user.id, {
    skip: !user.id
  });
  const accountMode = typeof user.account_mode === 'string' ? user.account_mode.toLowerCase() : '';
  const showMoLinkFallback = accountMode === 'check' && !pendingScopeEmail;
  const showMotiocompLinkFallback = accountMode === 'motiocomp' && !pendingScopeEmail;

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <input
          type="checkbox"
          checked={selectedUsers.includes(user.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedUsers([...selectedUsers, user.id]);
            } else {
              setSelectedUsers(selectedUsers.filter(id => id !== user.id));
            }
          }}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900">{user.seat_info?.accountId || '-'}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-600 font-medium">
                {user.name?.charAt(0) || '?'}
              </span>
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {user.name || 'Unknown'}
            </div>
            <div className="text-sm text-gray-500">
              ID: {user.id}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">{user.email}</div>
        {pendingScopeEmail && (
          <div className="mt-1">
            <div className="text-xs text-orange-600 font-medium">Pending: {pendingScopeEmail.email}</div>
            <button
              onClick={() => handleCompleteScopeReportEmail(user.id)}
              disabled={isLoadingCompleteEmail}
              className="mt-1 text-xs px-2 py-0.5 bg-orange-100 text-orange-800 hover:bg-orange-200 rounded disabled:opacity-50"
              title="Update user email to pending email address"
            >
              {isLoadingCompleteEmail ? 'Updating...' : 'Update Email'}
            </button>
          </div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900">{user.seat_info?.purchasedSeats || 0}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`text-sm ${user.seat_info?.usedSeats === user.seat_info?.purchasedSeats && user.seat_info?.purchasedSeats > 0 ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
          {user.seat_info?.usedSeats || 0}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <CreditsUsedCell user={user} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <OrganizationsCell user={user} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-900">
          {(() => {
            const mode = typeof user.account_mode === 'string' ? user.account_mode.toLowerCase() : null;
            if (!mode) return 'None';
            if (mode === 'planning') return 'Planning';
            if (mode === 'execution') return 'Execution';
            if (mode === 'check') return 'Mo';
            if (mode === 'motiocomp') return 'Motiocomp';
            return mode.charAt(0).toUpperCase() + mode.slice(1).toLowerCase();
          })()}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {(() => {
          const industry = typeof user.industry === 'string' ? user.industry.toLowerCase().trim() : '';
          if (industry === 'software' || industry) {
            return (
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-sky-100 text-sky-800">
                {industry === 'software' ? 'Software' : industry.charAt(0).toUpperCase() + industry.slice(1)}
              </span>
            );
          }
          return <span className="text-sm text-gray-400">None</span>;
        })()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <AccountStatusCell userId={user.id} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(user.created_at)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex flex-col space-y-2">
          <div className="flex space-x-2">
            <button
              onClick={() => handleGetVerificationUrl(user.id)}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
              title="Get verification email URL"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </button>
            {pendingScopeEmail && (
              <>
                {pendingScopeEmail.flow !== 'reshuffle' && (
                  <button
                    onClick={() => handleGetScopeReportLink(user.id, pendingScopeEmail.flow)}
                    disabled={isLoadingScopeReportLink}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200 disabled:opacity-50"
                    title={
                      pendingScopeEmail.flow === 'check'
                        ? 'Copy Mo portal link (same as email for /mo flow)'
                        : pendingScopeEmail.flow === 'motiocomp'
                        ? 'Copy Motiocomp portal link (same as email for /motiocomp flow)'
                        : pendingScopeEmail.flow === 'plan'
                        ? 'Copy plan report link (same as email for /plan flow)'
                        : pendingScopeEmail.flow === 'trial'
                        ? 'Copy trial report link (same as email for /trial flow)'
                        : 'Copy scope creep report link (same as email)'
                    }
                  >
                    {isLoadingScopeReportLink
                      ? '…'
                      : pendingScopeEmail.flow === 'check'
                      ? 'Mo link'
                      : pendingScopeEmail.flow === 'motiocomp'
                      ? 'Motiocomp link'
                      : pendingScopeEmail.flow === 'plan'
                      ? 'Plan link'
                      : pendingScopeEmail.flow === 'trial'
                      ? 'Trial link'
                      : 'Scope creep link'}
                  </button>
                )}
                {pendingScopeEmail.flow === 'reshuffle' && (
                  <button
                    onClick={() => handleGetReshuffleReportLink(user.id)}
                    disabled={isLoadingReshuffleReportLink}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800 hover:bg-violet-200 disabled:opacity-50"
                    title="Copy reshuffle report link (same as email for reshuffle flow)"
                  >
                    {isLoadingReshuffleReportLink ? '…' : 'Reshuffle link'}
                  </button>
                )}
              </>
            )}
            {showMoLinkFallback && (
              <button
                onClick={() => handleGetScopeReportLink(user.id, 'check')}
                disabled={isLoadingScopeReportLink}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200 disabled:opacity-50"
                title="Copy Mo portal link (same as email for /mo flow)"
              >
                {isLoadingScopeReportLink ? '…' : 'Mo link'}
              </button>
            )}
            {showMotiocompLinkFallback && (
              <button
                onClick={() => handleGetScopeReportLink(user.id, 'motiocomp')}
                disabled={isLoadingScopeReportLink}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50"
                title="Copy Motiocomp portal link (same as email for /motiocomp flow)"
              >
                {isLoadingScopeReportLink ? '…' : 'Motiocomp link'}
              </button>
            )}
            {pendingStartupEmail && (
              <button
                onClick={() => handleGetStartupReportLink(user.id)}
                disabled={isLoadingStartupReportLink}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 hover:bg-teal-200 disabled:opacity-50"
                title="Copy startup report link (same as email for /startup flow)"
              >
                {isLoadingStartupReportLink ? '…' : 'Startup link'}
              </button>
            )}
            {(pendingRiskintelligenceEmail || user.onboarding_source === 'riskintelligence') && (
              <button
                onClick={() => handleGetRiskIntelligenceReportLink(user.id)}
                disabled={isLoadingRiskIntelligenceReportLink}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50"
                title="Copy risk intelligence report link (same as email for /riskintelligence flow)"
              >
                {isLoadingRiskIntelligenceReportLink ? '…' : 'Risk link'}
              </button>
            )}
            {pendingTrialResourcesEmail && (
              <button
                onClick={() => handleGetTrialResourcesLink(user.id)}
                disabled={isLoadingTrialResourcesLink}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 hover:bg-cyan-200 disabled:opacity-50"
                title={
                  pendingTrialResourcesEmail.flow === 'trial_res_account'
                    ? 'Copy account setup + resources link (user opted to complete account from resources box)'
                    : 'Copy trial resources link (same as link in the resources email)'
                }
              >
                {isLoadingTrialResourcesLink
                  ? '…'
                  : pendingTrialResourcesEmail.flow === 'trial_res_account'
                  ? 'Account link'
                  : 'Resources link'}
              </button>
            )}
            <button
              onClick={() => handleResetPassword(user.id)}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 hover:bg-purple-200"
              title="Reset password"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </button>
            <button
              onClick={() => handleGenerateTemporaryPassword(user.id)}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 hover:bg-orange-200"
              title="Generate temporary access password (5 minutes)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </button>
            <AccountStatusButton user={user} />
            <button
              onClick={() => handleDeleteUser(user.id)}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200"
            >
              Delete
            </button>
          </div>
          <div className="flex space-x-2">
            <PlanStatusButton userId={user.id} onOpenModal={handleOpenEnterprisePlan} user={user} />
            <button
              onClick={() => onEditSeats(user.id)}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 hover:bg-teal-200"
              title="Edit seats"
            >
              🪑 Edit Seats
            </button>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                console.log('🔘 Support button clicked for user:', user.id, 'is_support_member:', user.is_support_member);
                handleToggleSupport(user.id);
              }}
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                user.is_support_member === 1
                  ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 font-semibold border-2 border-purple-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={user.is_support_member === 1 ? 'Click to remove Motionode Support role' : 'Click to set as Motionode Support'}
            >
              {(() => {
                const text = user.is_support_member === 1 ? '✓ Motionode Support' : 'Set as Support';
                if (user.id === 890) console.log('🎨 Rendering Support button for 890:', user.is_support_member, '→', text);
                return text;
              })()}
            </button>
            <button
              onClick={() => {
                console.log('🔘 Sales button clicked for user:', user.id, 'is_sales_member:', user.is_sales_member);
                handleToggleSales(user.id);
              }}
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                user.is_sales_member === 1
                  ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 font-semibold border-2 border-blue-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={user.is_sales_member === 1 ? 'Click to remove Motionode Sales role' : 'Click to set as Motionode Sales'}
            >
              {(() => {
                const text = user.is_sales_member === 1 ? '✓ Motionode Sales' : 'Set as Sales';
                if (user.id === 890) console.log('🎨 Rendering Sales button for 890:', user.is_sales_member, '→', text);
                return text;
              })()}
            </button>
            <button
              onClick={() => {
                console.log('🔘 Stripe Testing button clicked for user:', user.id, 'is_stripe_testing:', user.is_stripe_testing);
                handleToggleStripeTesting(user.id);
              }}
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                user.is_stripe_testing === 1
                  ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 font-semibold border-2 border-yellow-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={user.is_stripe_testing === 1 ? 'Click to disable Stripe testing mode' : 'Click to enable Stripe testing mode'}
            >
              {user.is_stripe_testing === 1 ? '✓ Stripe Testing' : 'Set as Testing'}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
};

// Edit Seats Modal Component
interface EditSeatsModalProps {
  userId: number;
  onClose: () => void;
}

const EditSeatsModal: React.FC<EditSeatsModalProps> = ({ userId, onClose }) => {
  const { data: seatInfo, isLoading: isLoadingSeatInfo, refetch } = useGetUserSeatInfoQuery(userId, {
    skip: !userId
  });
  const { data: accountInfo } = useGetUserAccountInfoQuery(userId, { skip: !userId });
  const [updateSeats, { isLoading: isUpdating }] = useUpdateUserSeatsMutation();
  const [createInvoice, { isLoading: isCreatingInvoice }] = useCreateEnterpriseInvoiceMutation();
  const [newSeatCount, setNewSeatCount] = useState<string>('');
  const [error, setError] = useState<string>('');
  
  // Enterprise billing options
  const [billingType, setBillingType] = useState<'PER_SEAT' | 'CUSTOM_TOTAL'>('PER_SEAT');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [periodMonths, setPeriodMonths] = useState<string>('');
  const [invoicePaid, setInvoicePaid] = useState<boolean>(false);

  // Initialize seat count when data loads
  useEffect(() => {
    if (seatInfo && !newSeatCount) {
      setNewSeatCount(seatInfo.purchasedSeats.toString());
    }
  }, [seatInfo, newSeatCount]);

  const currentSeats = seatInfo?.purchasedSeats || 0;
  const usedSeats = seatInfo?.usedSeats || 0;
  const parsedNewCount = parseInt(newSeatCount) || 0;
  const isAdding = parsedNewCount > currentSeats;
  const isReducing = parsedNewCount < currentSeats;
  const seatDifference = Math.abs(parsedNewCount - currentSeats);
  const isEnterprise = accountInfo?.subscription_id === 2;
  const SEAT_PRICE = 39; // $39 per seat

  // Validation
  const validateSeatCount = (count: number): string | null => {
    if (count < 0) return 'Seat count cannot be negative';
    if (count > 0 && count < 5) return 'Minimum seat count is 5';
    if (count > 0 && count % 5 !== 0) return 'Seat count must be a multiple of 5';
    if (count < usedSeats) return `Cannot reduce below ${usedSeats} seats (currently in use)`;
    return null;
  };

  const validationError = newSeatCount ? validateSeatCount(parsedNewCount) : null;
  
  // Custom total validation for enterprise
  const customBillingValid = billingType === 'CUSTOM_TOTAL' 
    ? (customAmount && parseFloat(customAmount) > 0 && periodMonths && parseInt(periodMonths) > 0)
    : true;

  const handleQuickAction = (action: 'add5' | 'remove5' | 'add10' | 'remove10') => {
    let newCount = parsedNewCount;
    switch (action) {
      case 'add5':
        newCount = parsedNewCount + 5;
        break;
      case 'remove5':
        newCount = Math.max(0, parsedNewCount - 5);
        break;
      case 'add10':
        newCount = parsedNewCount + 10;
        break;
      case 'remove10':
        newCount = Math.max(0, parsedNewCount - 10);
        break;
    }
    setNewSeatCount(newCount.toString());
  };

  const handleSubmit = async () => {
    setError('');
    
    if (validationError) {
      setError(validationError);
      return;
    }

    if (parsedNewCount === currentSeats) {
      onClose();
      return;
    }

    // For enterprise accounts adding seats with custom billing, use the createInvoice mutation
    if (isEnterprise && isAdding && billingType === 'CUSTOM_TOTAL') {
      if (!customAmount || !periodMonths) {
        setError('Please enter both the total amount and period for custom billing');
        return;
      }
      
      try {
        const result = await createInvoice({
          userId,
          seats: seatDifference,
          billingType: 'CUSTOM_TOTAL',
          customAmount: parseFloat(customAmount),
          periodMonths: parseInt(periodMonths),
          description: `${seatDifference} seats for ${periodMonths} month${parseInt(periodMonths) > 1 ? 's' : ''}`
        }).unwrap();
        
        const expiresText = result.expiresAt 
          ? `\nExpires: ${formatDateEST(result.expiresAt)}`
          : '';
        
        alert(`✅ Invoice created successfully!\n\nInvoice ID: ENT-${result.invoiceId.toString().padStart(5, '0')}\nSeats: +${seatDifference}\nAmount: $${result.amount}\nPeriod: ${periodMonths} month${parseInt(periodMonths) > 1 ? 's' : ''}${expiresText}\n\nYou can mark this invoice as paid in the Billing → Enterprise Invoices tab.`);
        refetch();
        onClose();
      } catch (err: any) {
        console.error('Failed to create invoice:', err);
        const errorMessage = err?.data?.message || err?.message || 'Failed to create invoice';
        setError(errorMessage);
      }
      return;
    }

    // Standard flow for per-seat or non-enterprise
    try {
      const updateOptions: any = {};
      if (isEnterprise && isAdding) {
        updateOptions.invoicePaid = invoicePaid;
        if (billingType === 'PER_SEAT' && periodMonths) {
          updateOptions.periodMonths = parseInt(periodMonths);
        }
        updateOptions.billingType = billingType;
        if (billingType === 'CUSTOM_TOTAL' && customAmount) {
          updateOptions.customAmount = parseFloat(customAmount);
        }
      }
      
      const result = await updateSeats({ 
        id: userId, 
        newSeatCount: parsedNewCount,
        ...updateOptions
      }).unwrap();
      
      let message = result.message;
      if (result.isEnterprise && result.invoiceId && isAdding) {
        message += `\n\nEnterprise Invoice ID: ENT-${result.invoiceId.toString().padStart(5, '0')}`;
        message += `\nAmount: $${(seatDifference * SEAT_PRICE).toFixed(2)}`;
        message += '\n\nYou can view and mark this invoice as paid in the Billing → Enterprise Invoices tab.';
      } else if (result.proratedAmount && result.proratedAmount > 0) {
        message += `\n\nProrated amount charged: $${result.proratedAmount.toFixed(2)}`;
      }
      
      alert(message);
      refetch();
      onClose();
    } catch (err: any) {
      console.error('Failed to update seats:', err);
      const errorMessage = err?.data?.message || err?.message || 'Failed to update seats';
      setError(errorMessage);
    }
  };

  // Calculate amounts for display
  const perSeatAmount = seatDifference * SEAT_PRICE;
  const customAmountNum = parseFloat(customAmount) || 0;
  const periodMonthsNum = parseInt(periodMonths) || 0;
  const perSeatPerMonth = customAmountNum > 0 && seatDifference > 0 && periodMonthsNum > 0 
    ? (customAmountNum / seatDifference / periodMonthsNum).toFixed(2)
    : '0';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Edit Seats</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isLoadingSeatInfo ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Current Stats */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{currentSeats}</div>
                <div className="text-xs text-gray-500">Purchased</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-600">{usedSeats}</div>
                <div className="text-xs text-gray-500">In Use</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{currentSeats - usedSeats}</div>
                <div className="text-xs text-gray-500">Available</div>
              </div>
            </div>

            {/* New Seat Count Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Seat Count:
              </label>
              <input
                type="number"
                min="0"
                step="5"
                value={newSeatCount}
                onChange={(e) => {
                  setNewSeatCount(e.target.value);
                  setError('');
                }}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  validationError ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {validationError && (
                <p className="mt-1 text-sm text-red-600">{validationError}</p>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleQuickAction('add5')}
                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-full hover:bg-green-200"
              >
                +5
              </button>
              <button
                onClick={() => handleQuickAction('add10')}
                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-full hover:bg-green-200"
              >
                +10
              </button>
              <button
                onClick={() => handleQuickAction('remove5')}
                disabled={parsedNewCount < 5}
                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-full hover:bg-red-200 disabled:opacity-50"
              >
                -5
              </button>
              <button
                onClick={() => handleQuickAction('remove10')}
                disabled={parsedNewCount < 10}
                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-full hover:bg-red-200 disabled:opacity-50"
              >
                -10
              </button>
            </div>

            {/* Enterprise Badge */}
            {isEnterprise && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-700">
                  <strong>🏢 Enterprise Account</strong> - This account uses manual billing. Seat changes will create an internal invoice instead of charging Stripe.
                </p>
              </div>
            )}

            {/* Enterprise Billing Options - Only show when adding seats */}
            {isEnterprise && isAdding && seatDifference > 0 && !validationError && (
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4">
                <div>
                  <label className="block text-sm font-medium text-purple-800 mb-2">
                    Billing Type:
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="billingType"
                        value="PER_SEAT"
                        checked={billingType === 'PER_SEAT'}
                        onChange={() => setBillingType('PER_SEAT')}
                        className="mr-2"
                      />
                      <span className="text-sm text-purple-700">Per Seat ($39/seat)</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="billingType"
                        value="CUSTOM_TOTAL"
                        checked={billingType === 'CUSTOM_TOTAL'}
                        onChange={() => setBillingType('CUSTOM_TOTAL')}
                        className="mr-2"
                      />
                      <span className="text-sm text-purple-700">Custom Total for Period</span>
                    </label>
                  </div>
                </div>

                {/* Period Selection for PER_SEAT (optional) */}
                {billingType === 'PER_SEAT' && (
                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-2">
                      Billing Period (optional):
                    </label>
                    <select
                      value={periodMonths}
                      onChange={(e) => setPeriodMonths(e.target.value)}
                      className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">One-time (no period)</option>
                      <option value="1">1 Month</option>
                      <option value="2">2 Months</option>
                      <option value="3">3 Months</option>
                      <option value="6">6 Months</option>
                      <option value="12">12 Months (1 Year)</option>
                      <option value="24">24 Months (2 Years)</option>
                    </select>
                    <p className="mt-1 text-xs text-purple-600">Select a period if this invoice should recur or expire after a specific time</p>
                  </div>
                )}

                {/* Custom Total Fields */}
                {billingType === 'CUSTOM_TOTAL' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-purple-800 mb-2">
                        Period (Months):
                      </label>
                      <select
                        value={periodMonths}
                        onChange={(e) => setPeriodMonths(e.target.value)}
                        className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">Select period...</option>
                        <option value="1">1 Month</option>
                        <option value="2">2 Months</option>
                        <option value="3">3 Months</option>
                        <option value="6">6 Months</option>
                        <option value="12">12 Months (1 Year)</option>
                        <option value="24">24 Months (2 Years)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-purple-800 mb-2">
                        Total Amount ($):
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder="e.g., 2000"
                        className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </>
                )}

                {/* Invoice Payment Status */}
                <div className="flex items-center justify-between p-3 bg-white border border-purple-200 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-purple-800">
                      Invoice Status
                    </label>
                    <p className="text-xs text-purple-600">Mark this invoice as already paid?</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="invoicePaid"
                        checked={invoicePaid === true}
                        onChange={() => setInvoicePaid(true)}
                        className="form-radio text-green-600"
                      />
                      <span className="ml-2 text-sm text-green-700">Paid</span>
                    </label>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="invoicePaid"
                        checked={invoicePaid === false}
                        onChange={() => setInvoicePaid(false)}
                        className="form-radio text-yellow-600"
                      />
                      <span className="ml-2 text-sm text-yellow-700">Pending</span>
                    </label>
                  </div>
                </div>

                {/* Invoice Summary */}
                <div className="p-3 bg-white border border-purple-200 rounded-lg">
                  <p className="text-sm font-medium text-purple-800 mb-1">Invoice Summary:</p>
                  <p className="text-sm text-purple-700">
                    {billingType === 'PER_SEAT' ? (
                      <>
                        {seatDifference} seats × $39/seat = <strong>${perSeatAmount.toFixed(2)}</strong>
                        {periodMonthsNum > 0 && (
                          <span className="text-xs block mt-1 text-purple-600">
                            Period: {periodMonthsNum} month{periodMonthsNum > 1 ? 's' : ''}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        {seatDifference} seats for {periodMonths || '?'} month{periodMonthsNum > 1 ? 's' : ''} = <strong>${customAmount || '?'}</strong>
                        {periodMonthsNum > 0 && customAmountNum > 0 && (
                          <span className="text-xs block mt-1">
                            (${perSeatPerMonth}/seat/month)
                          </span>
                        )}
                      </>
                    )}
                  </p>
                  <p className="text-xs text-purple-600 mt-2">
                    Status: <strong>{invoicePaid ? 'Paid' : 'Pending'}</strong>
                  </p>
                </div>
              </div>
            )}

            {/* Change Preview (for non-enterprise or reducing) */}
            {parsedNewCount !== currentSeats && !validationError && (!isEnterprise || isReducing) && (
              <div className={`p-4 rounded-lg ${isAdding ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-lg font-medium ${isAdding ? 'text-green-700' : 'text-yellow-700'}`}>
                    {isAdding ? '➕' : '➖'} {seatDifference} seats
                  </span>
                </div>
                <p className={`text-sm ${isAdding ? 'text-green-600' : 'text-yellow-600'}`}>
                  {isAdding 
                    ? `The prorated amount for ${seatDifference} additional seat(s) will be charged immediately.`
                    : isEnterprise
                    ? `Removing ${seatDifference} seat(s). A credit of $${perSeatAmount.toFixed(2)} will be applied to the next invoice.`
                    : `Removing ${seatDifference} seat(s). No refund will be issued. The lower price will take effect on the next billing cycle.`
                  }
                </p>
                {isReducing && parsedNewCount >= usedSeats && (
                  <p className="text-sm text-yellow-600 mt-1">
                    <strong>Note:</strong> You can only remove {currentSeats - usedSeats} seat(s) maximum because {usedSeats} seat(s) are currently in use.
                  </p>
                )}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Stripe Info */}
            {seatInfo?.stripeSubscriptionId && !isEnterprise && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>Stripe Subscription:</strong> {seatInfo.stripeSubscriptionId.substring(0, 20)}...
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isUpdating || isCreatingInvoice || !!validationError || parsedNewCount === currentSeats || (isEnterprise && isAdding && billingType === 'CUSTOM_TOTAL' && !customBillingValid)}
                className={`px-4 py-2 text-white rounded-lg ${
                  isAdding 
                    ? isEnterprise ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700' 
                    : isReducing 
                    ? 'bg-yellow-600 hover:bg-yellow-700' 
                    : 'bg-primary-600 hover:bg-primary-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isUpdating || isCreatingInvoice
                  ? 'Processing...' 
                  : parsedNewCount === currentSeats 
                  ? 'No Change' 
                  : isAdding 
                  ? isEnterprise 
                    ? billingType === 'CUSTOM_TOTAL'
                      ? 'Create Custom Invoice'
                      : 'Add Seats & Create Invoice' 
                    : 'Add Seats & Charge' 
                  : 'Reduce Seats'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users; 